import { cpfValidator } from 'helpers/cpfValidator';
import { Dispatch, SetStateAction, useState } from 'react';
import * as yup from 'yup';
import { cnpjValidator } from 'helpers/cnpjValidator';
import { Doctor } from 'types/doctor';
import { sanitizeDocument } from 'helpers/sanitizeDocument';

export type DoctorValidation = {
  document_number?: string;
  rg?: string;
  name?: string;
  email?: string;
  person_type?: string;
};

type UseDoctorValidation = [
  DoctorValidation,
  Dispatch<SetStateAction<DoctorValidation>>,
  (doctor: Doctor) => Promise<void>,
];

export function useDoctorValidation(): UseDoctorValidation {
  async function handleValidation(doctor: Doctor) {
    const schema = yup.object().shape({
      email: yup.string().required('O e-mail é obrigatório').email('O e-mail deve ser válido'),
      rg: yup.string().required('RG é obrigatório'),
      document_number: yup
        .string()
        .transform((value, originalValue) => {
          return sanitizeDocument(originalValue);
        })
        .required('O CPF/CNPJ é obrigatório')
        .test('is-valid-cpf-cnpj', 'CPF/CNPJ inválido', value => {
          if (!value) return false;

          const cnpj = cnpjValidator(value);
          const cpf = cpfValidator(value);

          return cnpj || cpf;
        }),
      person_type: yup.string().required('O tipo de pessoa é obrigatório'),
      name: yup.string().required('O nome é obrigatório').min(10, 'O nome deve ter no mínimo 10 caracteres'),
    });

    try {
      await schema.validate(doctor);
    } catch (err) {
      const error = err as yup.ValidationError;
      if (error.path && error.message) {
        setValidation({
          [error.path]: error.message,
        });
      }
      throw new Error(err as string);
    }
  }

  const [validation, setValidation] = useState<DoctorValidation>({});
  return [validation, setValidation, handleValidation];
}
